import { useState } from 'react';
import { connect } from 'react-redux';

import { isSameDay } from 'shared/util/datetime-fns';
import { type CheckInCheckOutErrorTexts, validateCheckInCheckOut } from '@/utils/dateValidationErrors';
import { trackShowAvailability } from '@/utils/tracking/finnAnalytics';
import { fetchHotel } from '@/state/hotelPageActions';
import { actions as filterActions } from '@/state/filterActions';

import type { AccommodationState, CheckInCheckOut, RoomWithPeople } from '@/interfaces';
import HotelPageSearchFormPreview from './components/hotelPageSearchFormPreview/HotelPageSearchFormPreview';
import { CheckInCheckOutCalendar } from '@/containers/checkInCheckOutCalendar/CheckInCheckOutCalendar';
import { TravellersPicker } from '@/containers/travellersPicker/TravellersPicker';
import { EditPanel } from '@/components/editPanel/EditPanel';

interface Props {
    hotelKey: string;
    hotelId: number;
    allowUpdateIfUnchanged?: boolean;
}

interface StateProps {
    initialCheckInDate?: Date | number;
    initialCheckOutDate?: Date | number;
    initialRooms: readonly RoomWithPeople[];
}

interface DispatchProps {
    updateDates: (dates: CheckInCheckOut) => void;
    updateRooms: (rooms: readonly RoomWithPeople[]) => void;
    triggerFetch: () => void;
    clearFilters: () => void;
}

export type HotelPageSearchFormProps = StateProps & DispatchProps & Props;

const validationErrorTexts: CheckInCheckOutErrorTexts = {
    missingCheckInDate: 'Velg en gyldig dato for innsjekk',
    missingCheckOutDate: 'Velg en gyldig dato for utsjekk',
    invalidCheckInDate: 'Dato for innsjekk kan ikke være før idag',
    invalidCheckOutDate: 'Dato for utsjekk må være etter dato for innsjekk',
    tooManyDaysBetweenDates: 'Oppholdet kan ikke vare mer enn 30 dager',
};

export const HotelPageSearchForm = ({
    allowUpdateIfUnchanged = false,
    initialCheckInDate,
    initialCheckOutDate,
    initialRooms,
    updateDates,
    updateRooms,
    triggerFetch,
    clearFilters,
}: HotelPageSearchFormProps) => {
    const [checkInDate, setCheckInDate] = useState<Date | number | undefined>(initialCheckInDate);
    const [checkOutDate, setCheckOutDate] = useState<Date | number | undefined>(initialCheckOutDate);
    const [hotelRooms, setHotelRooms] = useState(initialRooms);
    const [showErrors, setShowErrors] = useState(false);

    const validationErrors = validateCheckInCheckOut(validationErrorTexts, checkInDate, checkOutDate);
    const calendarErrors = showErrors ? validationErrors : [];

    const isChanged = (): boolean =>
        !isSameDay(checkInDate, initialCheckInDate) ||
        !isSameDay(checkOutDate, initialCheckOutDate) ||
        JSON.stringify(hotelRooms) !== JSON.stringify(initialRooms);

    const updateSearch = (): void => {
        if (validationErrors.length > 0) {
            setShowErrors(true);
        } else if (allowUpdateIfUnchanged || isChanged()) {
            updateDates({ checkInDate, checkOutDate });
            updateRooms(hotelRooms);
            trackShowAvailability();
            triggerFetch();
            clearFilters();
        }
    };

    return (
        <EditPanel
            open={!checkInDate || !checkOutDate}
            preview={<HotelPageSearchFormPreview />}
            className="availability-panel u-mb16--lt768"
            openOnDesktop>
            <div className="grid u-pv16 hotel-page-search-form">
                <div className="grid__unit u-r-size4of5 u-pr16--gt768">
                    <div className="grid__unit u-r-size2of3">
                        <CheckInCheckOutCalendar
                            checkInDate={checkInDate}
                            checkOutDate={checkOutDate}
                            setCheckInDate={setCheckInDate}
                            setCheckOutDate={setCheckOutDate}
                            errors={calendarErrors}
                        />
                    </div>
                    <div className="grid__unit u-r-size1of3 u-pl16--gt768">
                        <TravellersPicker rooms={hotelRooms} updateRooms={setHotelRooms} />
                    </div>
                </div>
                <div className="grid__unit u-r-size1of5 u-mt16 u-pt4">
                    <button
                        type="button"
                        className="u-size1of1 button button--no-break u-ph0"
                        data-testid="searchButton"
                        value="Søk"
                        onClick={updateSearch}>
                        Sjekk tilgjengelighet
                    </button>
                </div>
            </div>
        </EditPanel>
    );
};

const mapStateToProps = (state: AccommodationState): StateProps => ({
    initialCheckInDate: state.filters.checkInDate,
    initialCheckOutDate: state.filters.checkOutDate,
    initialRooms: state.filters.rooms,
});

const mapDispatchToProps = {
    updateDates: filterActions.changeDates,
    updateRooms: filterActions.changeRooms,
    triggerFetch: fetchHotel,
    clearFilters: filterActions.clearHotelPageCustom,
};

export default connect(mapStateToProps, mapDispatchToProps)(HotelPageSearchForm);
